import { fetchSqlDataWithQuery } from './db.api';

/**
 * Get the next value for a serial sequence in a PostgreSQL table without incrementing it.
 * @param tableName - Name of the table
 * @param columnName - Name of the serial column (default: 'id')
 * @param incrementBy - Increment step (default: 1)
 * @param schemaName - Name of the schema (default: 'public')
 * @param database - Optional database name
 * @returns next_value (number) or null if not found
 */
export const getTableNextSequenceValue = async (
  tableName: string,
  columnName = 'id',
  incrementBy = 1,
  schemaName = 'public',
  database?: string | null
) => {
  // Get the sequence name for the table/column
  const seqNameQuery = `SELECT pg_get_serial_sequence('${tableName}', '${columnName}') AS seqname;`;
  const seqNameResp = await fetchSqlDataWithQuery(seqNameQuery, database);
  const seqFullName = seqNameResp?.[0]?.seqname;
  if (!seqFullName) return null;
  const [seqSchema, seqName] = seqFullName.split('.');
  const query = `SELECT \
    CASE \
      WHEN last_value IS NULL THEN start_value \
      ELSE last_value + increment_by \
    END AS next_value \
  FROM pg_sequences \
  WHERE schemaname = '${seqSchema}' \
    AND sequencename = '${seqName}';`;
  const response = await fetchSqlDataWithQuery(query, database);
  return response?.[0]?.next_value;
};

/**
 * Get one or more next values from a PostgreSQL sequence using nextval().
 * WARNING: nextval() increments the sequence atomically on the database.
 * @param sequenceName Fully-qualified sequence name or simple name (allowed chars: [A-Za-z0-9_\.])
 * @param count Number of values to fetch (default 1)
 * @param database Optional database name
 * @returns number[] of next values (length == count)
 */
export const getNextSequenceValues = async (
  sequenceName: string,
  count = 1,
  database?: string | null
): Promise<number[]> => {
  // Basic validation to avoid injection via sequence name
  const isSafe = /^[A-Za-z0-9_\.]+$/.test(sequenceName);
  if (!isSafe) {
    throw new Error('Invalid sequence name');
  }
  const safeSeq = sequenceName;

  const query = count > 1
    ? `SELECT nextval('${safeSeq}') AS next_value FROM generate_series(1, ${Number(count)})`
    : `SELECT nextval('${safeSeq}') AS next_value`;

  const resp = await fetchSqlDataWithQuery(query, database);
  if (!resp) return [];
  if (count > 1) {
    return resp.map((r: any) => Number(r?.next_value)).filter((n: any) => Number.isFinite(n));
  }
  const single = Array.isArray(resp) && resp[0] ? Number(resp[0].next_value) : undefined;
  return Number.isFinite(single) ? [single as number] : [];
};

/**
 * Convenience wrapper to fetch a single nextval from a sequence.
 */
export const getNextSequenceValue = async (
  sequenceName: string,
  database?: string | null
): Promise<number | null> => {
  const vals = await getNextSequenceValues(sequenceName, 1, database);
  return vals.length ? vals[0] : null;
};

/**
 * Generate a Work Permit reference number like WP-YYYY-#### using the next table sequence value.
 * Uses getTableNextSequenceValue to peek the upcoming id and pads to 4 digits.
 */
export const generateWorkPermitRefNum = async (): Promise<string> => {
  try {
    const nextId = await getTableNextSequenceValue('hse_workpermits', 'id');
    const year = new Date().getFullYear();
    const seq = String(nextId ?? 1).padStart(4, '0');
    return `WP-${year}-${seq}`;
  } catch (e) {
    // Fallback with timestamp to avoid blocking creation
    const year = new Date().getFullYear();
    const ts = Date.now().toString().slice(-4);
    return `WP-${year}-${ts}`;
  }
};

/**
 * Generate a Construction Machine Allocation reference number like MAC-REQ-YYYY-####.
 *
 * NOT: ID sekansindan bagimsiz calisir; sadece veritabaninda GERCEKTEN kayitli olan
 * ref_num degerlerine bakar ve ilgili yil icin MAX + 1 hesaplar.
 * Boylece basarisiz insert/rollback denemeleri numarayi bosuna ilerletmez.
 */
export const generateConstMachineAllocationRefNum = async (): Promise<string> => {
  const year = new Date().getFullYear();
  try {
    // Mevcut kayitlar icindeki en yuksek sira numarasini bul
    const sql = `
      SELECT COALESCE(
        MAX(CAST(split_part(ref_num, '-', 4) AS INTEGER)),
        0
      ) + 1 AS next_no
      FROM const_machine_allocation
      WHERE ref_num LIKE 'MAC-REQ-${year}-%';
    `;
    const rows = await fetchSqlDataWithQuery(sql);
    const arr = Array.isArray(rows) ? rows : (rows?.data || []);
    const nextNoRaw = arr && arr[0] && (arr[0] as any).next_no;
    const nextNo = Number.isFinite(Number(nextNoRaw)) ? Number(nextNoRaw) : 1;
    const seq = String(nextNo).padStart(4, '0');
    return `MAC-REQ-${year}-${seq}`;
  } catch (e) {
    // Fallback: yine de benzersiz ve artan gibi davranan bir numara uret
    const ts = Date.now().toString().slice(-4);
    return `MAC-REQ-${year}-${ts}`;
  }
};
